<template>
  <v-card>
    <v-card-title>
      Assets Count Management
      <v-spacer></v-spacer>
      <v-btn color="success" dark @click="showCreateDialog" class="mr-2">
        <v-icon left>mdi-plus</v-icon>
        Create Assets Count
      </v-btn> 
    </v-card-title>

    <!-- <v-card-actions class="py-2">
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Search Session"
        dense
        hide-details
        class="mx-2"
        style="max-width: 300px"
      /> 
        <v-icon left>mdi-file-excel</v-icon>
        Export CSV
      </v-btn>
    </v-card-actions> -->

    <!-- Filters Section -->
    <v-card-text>
      <v-row>
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.department"
            :items="departments"
            item-text="name"
            item-value="id"
            label="Department"
            clearable
            dense
            outlined
            @change="applyFilters"
          >
            <template v-slot:prepend-item>
              <v-list-item @click="filters.department = null">
                <v-list-item-content>
                  <v-list-item-title>All Departments</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
            </template>
          </v-select>
        </v-col>
        
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.location"
            :items="locations"
            item-text="name"
            item-value="id"
            label="Location"
            clearable
            dense
            outlined
            @change="applyFilters"
          >
            <template v-slot:prepend-item>
              <v-list-item @click="filters.location = null">
                <v-list-item-content>
                  <v-list-item-title>All Locations</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
            </template>
          </v-select>
        </v-col>
        
        <v-col cols="12" md="3">
          <v-menu
            ref="dateMenu"
            v-model="dateMenu"
            :close-on-content-click="false"
            :return-value.sync="filters.date"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="filters.date"
                label="Count Date"
                prepend-icon="mdi-calendar"
                readonly
                dense
                outlined
                clearable
                @click:clear="clearDate"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="filters.date"
              no-title
              scrollable
            >
              <v-spacer></v-spacer>
              <v-btn
                text
                color="primary"
                @click="dateMenu = false"
              >
                Cancel
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="$refs.dateMenu.save(filters.date); applyFilters()"
              >
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
    </v-card-text>

    <v-simple-table>
      <thead>
        <tr>
          <th>
            <v-checkbox
              :input-value="selectedAll"
              @change="clickSelectedAll($event)"
              color="#6ec39b"
              class="ma-0 pa-0"
              hide-details
            />
          </th>
          <th>No.</th>
          <th class="order-by-row" @click="changeOrderBy('departmentName')">
            Department
            <v-icon v-if="orderBySelect.key === 'departmentName'" small>
              {{ orderBySelect.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
            </v-icon>
          </th>
          <th class="order-by-row" @click="changeOrderBy('locationName')">
            Location
            <v-icon v-if="orderBySelect.key === 'locationName'" small>
              {{ orderBySelect.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
            </v-icon>
          </th>
          <th class="order-by-row" @click="changeOrderBy('countDate')">
            Count Date
            <v-icon v-if="orderBySelect.key === 'countDate'" small>
              {{ orderBySelect.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
            </v-icon>
          </th>
          <th class="order-by-row" @click="changeOrderBy('totalAssets')">
            Total Assets
            <v-icon v-if="orderBySelect.key === 'totalAssets'" small>
              {{ orderBySelect.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
            </v-icon>
          </th>
          <th class="order-by-row" @click="changeOrderBy('countedAssets')">
            Counted
            <v-icon v-if="orderBySelect.key === 'countedAssets'" small>
              {{ orderBySelect.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
            </v-icon>
          </th>
          <th>Progress</th>
          <!-- <th>Actions</th> -->
        </tr>
      </thead>
      <tbody>
        <tr v-if="!filteredData.length">
          <td :colspan="9" class="text-center py-8">ไม่พบเอกสารที่ต้องการ</td>
        </tr>
        <tr
          v-for="(item, idx) in filteredData"
          :key="item.id"
        >
          <td>
            <v-checkbox
              :input-value="isChecked(item.id)"
              @change="toggleSelect(item)"
              color="#6ec39b"
              class="ma-0 pa-0"
              hide-details
            />
          </td>
          <td>{{ idx + 1 }}</td>
          <td>{{ item.department }}</td>
          <td>{{ item.location }}</td>
          <td>{{ formatDate(item.createAt) }}</td>
          <td>
            <v-chip small color="primary">{{ item.assetsTotal }}</v-chip>
          </td>
          <td>
            <v-chip 
              small 
              :color="item.assetsCount === item.assetsTotal ? 'success' : 'warning'"
            >
              {{ item.assetsCount }}
            </v-chip>
          </td>
          <td>
            <v-progress-linear
              :value="(item.assetsCount / item.assetsTotal) * 100"
              height="20"
              :color="item.assetsCount === item.assetsTotal ? 'success' : 'warning'"
            >
              <template v-slot:default="{ value }">
                <strong>{{ Math.ceil(value) }}%</strong>
              </template>
            </v-progress-linear>
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <!-- Session Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="800px">
      <v-card>
        <v-card-title>
          Session Count Details
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text v-if="selectedSession">
          <v-row>
            <v-col cols="6">
              <strong>Department:</strong> {{ getDepartmentName(selectedSession.departmentId) }}
            </v-col>
            <v-col cols="6">
              <strong>Location:</strong> {{ getLocationName(selectedSession.locationId) }}
            </v-col>
            <v-col cols="6">
              <strong>Count Date:</strong> {{ formatDate(selectedSession.countDate) }}
            </v-col>
            <v-col cols="6">
              <strong>Created By:</strong> {{ selectedSession.createdBy }}
            </v-col>
          </v-row>
          
          <v-divider class="my-4"></v-divider>
          
          <h3>Asset Count Summary</h3>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Count</th>
                  <th>Percentage</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total Assets</td>
                  <td>{{ selectedSession.totalAssets }}</td>
                  <td>100%</td>
                </tr>
                <tr>
                  <td>Counted Assets</td>
                  <td>{{ selectedSession.countedAssets }}</td>
                  <td>{{ Math.round((selectedSession.countedAssets / selectedSession.totalAssets) * 100) }}%</td>
                </tr>
                <tr>
                  <td>Missing Assets</td>
                  <td>{{ selectedSession.totalAssets - selectedSession.countedAssets }}</td>
                  <td>{{ Math.round(((selectedSession.totalAssets - selectedSession.countedAssets) / selectedSession.totalAssets) * 100) }}%</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Create Session Count Dialog -->
    <v-dialog v-model="createDialog" max-width="600px">
      <v-card>
        <v-card-title>
          Create New Fixed Asset Count
          <v-spacer></v-spacer>
          <v-btn icon @click="createDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="createForm" v-model="validForm">
            <v-row>
              <!-- <v-col cols="12" md="6">
                <v-select
                  v-model="newSession.departmentId"
                  :items="departments"
                  item-text="name"
                  item-value="id"
                  label="Department *"
                  :rules="[v => !!v || 'Department is required']"
                  outlined
                  dense
                  required
                ></v-select>
              </v-col> -->
              
              <v-col cols="12" md="6">
                <v-select
                  v-model="newSession.locationId"
                  :items="locations"
                  item-text="name"
                  item-value="id"
                  label="Location *"
                  :rules="[v => !!v || 'Location is required']"
                  outlined
                  dense
                  required
                ></v-select>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-menu
                  ref="createDateMenu"
                  v-model="createDateMenu"
                  :close-on-content-click="false"
                  :return-value.sync="newSession.countDate"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="newSession.countDate"
                      label="Count Date *"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      :rules="[v => !!v || 'Count date is required']"
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="newSession.countDate"
                    no-title
                    scrollable
                  >
                    <v-spacer></v-spacer>
                    <v-btn
                      text
                      color="primary"
                      @click="createDateMenu = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="$refs.createDateMenu.save(newSession.countDate)"
                    >
                      OK
                    </v-btn>
                  </v-date-picker>
                </v-menu>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="newSession.description"
                  label="Description"
                  outlined
                  dense
                  placeholder="Optional description"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="createDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="success"
            :disabled="!validForm || creatingSession"
            :loading="creatingSession"
            @click="createSessionCount"
          >
            Create Session
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'SessionCountTable',  data() {
    return {
      loading: false,
      dateMenu: false,
      detailsDialog: false,
      selectedSession: null,
      createDialog: false,
      createDateMenu: false,
      validForm: false,
      creatingSession: false,
      search: '',
      selectedAll: false,
      selectedList: [],
      newSession: {
        departmentId: null,
        locationId: null,
        countDate: null,
        description: ''
      },
      filters: {
        department: null,
        location: null,
        date: null
      },
      fixedAssetsCounts: []
    }
  },
    computed: {
    ...mapState({ 
      assetCounts: state => state.fixedAssets.assetCounts,
      departments: state => state.fixedAssets.departments,
      locations: state => state.fixedAssets.locations,
    }),
    
    filteredData() {
      let data = [...this.assetCounts]
      
      // Apply search filter
      if (this.search) {
        const s = this.search.toLowerCase()
        data = data.filter(item =>
          (item.departmentName || '').toLowerCase().includes(s) ||
          (item.locationName || '').toLowerCase().includes(s) ||
          (item.countDate || '').toLowerCase().includes(s) ||
          (item.description || '').toLowerCase().includes(s)
        )
      }
      
      // Apply dropdown filters
      if (this.filters.department) {
        data = data.filter(item => item.departmentId === this.filters.department)
      }
      
      if (this.filters.location) {
        data = data.filter(item => item.locationId === this.filters.location)
      }
      
      if (this.filters.date) {
        data = data.filter(item => item.countDate === this.filters.date)
      }
      
      return data
    },
    
    orderBySelect() {
      return this.$store.state.fixedAssets?.orderBySelect || { key: 'countDate', value: 'desc' };
    },  },

  watch: {
    selectedList() {
      this.selectedAll = this.selectedList.length === this.filteredData.length && this.filteredData.length > 0
    }
  },
    methods:{
    ...mapActions({
      fetchAssetsCount: 'fixedAssets/fetchAssetCount',
      fetchDepartments: 'fixedAssets/fetchDepartments',
      fetchLocations: 'fixedAssets/fetchLocations'
    }),
    
    async fetchFixedAssetsCounts() {
      this.loading = true
      try {
        const companyId = this.$route.params.companyId || localStorage.getItem('companyId')
        
        // Mock data - replace with actual Firebase query
         await this.$store.dispatch('fixedAssets/fetchFixedAssetsCounts', { companyId })
      } catch (error) {
        console.error('Error fetching session counts:', error)
      } finally {
        this.loading = false
      }
    },
    
    applyFilters() {
      // Filters are automatically applied through the computed property
      console.log('Filters applied:', this.filters)
    },
    
    clearDate() {
      this.filters.date = null
      this.applyFilters()
    }, 

    isChecked(sessionId) {
      return this.selectedList.find(x => x.id === sessionId)
    },

    toggleSelect(item) {
      const idx = this.selectedList.findIndex(x => x.id === item.id)
      if (idx === -1) this.selectedList.push(item)
      else this.selectedList.splice(idx, 1)
    },

    clickSelectedAll(checked) {
      if (checked) {
        this.selectedList = [...this.filteredData]
      } else {
        this.selectedList = []
      }
    },

    changeOrderBy(order) {
      let orderBySelect = { ...this.orderBySelect }
      if (orderBySelect.key !== order) {
        orderBySelect = { key: order, value: 'asc' }
      } else {
        if (orderBySelect.value === 'asc') {
          orderBySelect.value = 'desc'
        } else {
          orderBySelect = { key: 'countDate', value: 'desc' }
        }
      }
      // Update store with proper format
      this.$store.dispatch('fixedAssets/setOrderBySelect', {
        orderBySelect: orderBySelect,
      })
      console.log('Order changed:', orderBySelect)
      // Refetch data with new ordering
      this.$store.dispatch('fixedAssets/fetchFixedAssetsCounts', {
        companyId: this.$route.params.companyId || localStorage.getItem('companyId'),
      })
    },    
    formatDate(timestamp) {
      if (!timestamp) return ''
      
      // Handle Firebase Timestamp object
      if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
      }
      
      // Handle regular date string or Date object
      const date = new Date(timestamp)
      return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
      })
    },
    
    getDepartmentName(departmentId) {
      const dept = this.departments.find(d => d.id === departmentId)
      return dept ? dept.name : 'Unknown Department'
    },    
    getLocationName(locationId) {
      const loc = this.locations.find(l => l.id === locationId)
      return loc ? loc.name : 'Unknown Location'
    },

    showCreateDialog() {
      this.createDialog = true
      this.resetNewSession()
    },

    resetNewSession() {
      this.newSession = {
        departmentId: null,
        locationId: null,
        countDate: null,
        description: ''
      }
      if (this.$refs.createForm) {
        this.$refs.createForm.resetValidation()
      }
    },

    async createSessionCount() {
      if (!this.validForm) return

      this.creatingSession = true
      try {
        const companyId = this.$route.params.companyId || localStorage.getItem('companyId')
        
        // Get department and location names
        const department = this.departments.find(d => d.id === this.newSession.departmentId)
        const location = this.locations.find(l => l.id === this.newSession.locationId)
        
        // Create new session count object
        const newSessionCount = {
          id: Date.now().toString(), // Generate temporary ID
          departmentId: this.newSession.departmentId,
          departmentName: department?.name || 'Unknown Department',
          locationId: this.newSession.locationId,
          locationName: location?.name || 'Unknown Location',
          countDate: this.newSession.countDate,
          description: this.newSession.description,
          totalAssets: 0, // Will be calculated based on assets in this dept/location
          countedAssets: 0,
          createdBy: 'current.user@company.com', // Replace with actual user
          createdAt: new Date()
        }

        // TODO: Replace with actual Firebase create logic
        console.log('Creating new session count:', newSessionCount)
        
        // Add to local array for demo (replace with actual API call)
        this.fixedAssetsCounts.unshift(newSessionCount)
        
        // Close dialog and reset form
        this.createDialog = false
        this.resetNewSession()
        
        // Show success message
        this.$emit('session-created', newSessionCount)
        
      } catch (error) {
        console.error('Error creating session count:', error)
        alert('Error creating session count. Please try again.')
      } finally {
        this.creatingSession = false
      }
    }
  },
    mounted() {
    const companyId = this.$route.params.companyId || localStorage.getItem('companyId')
    if (companyId) {
      this.fetchDepartments({ companyId })
      this.fetchLocations({ companyId })
      this.fetchAssetsCount({ companyId }) 
      this.fetchFixedAssetsCounts()
    }
  }
}
</script>

<style scoped>
.order-by-row {
  cursor: pointer;
}

.order-by-row:hover {
  background-color: #f5f5f5;
}

.v-chip {
  font-weight: bold;
}

.v-progress-linear {
  border-radius: 10px;
}
</style>